import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { ErrorObject } from 'src/app/_models/error';
import {
  CalibrationNotification,
  CalibrationResults,
  CalibrationTypeEnum,
  FinaliseCalibrationInputSet,
  VoltageReadings,
  ActiveCalibrationSummary,
} from 'src/app/_models/calibration';
import { OxygenCalibration } from 'src/app/_models/oxygen-calibration';
import { CalibrationService } from 'src/app/_services/calibration.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ICalibrationDevice,
  IOxygenCalibrationDevice,
  IPhCalibrationDevice,
  ISpargingCalibrationDevice,
} from 'src/app/_models/experiment-data';
import { DataService } from 'src/app/_services/data.service';
import { ModalService } from 'src/app/_services/modal.service';
import { FormBase } from 'src/app/_models/form-base';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { stepValidator } from 'src/app/_helpers/step-validator.validator';
import { PhCalibration } from 'src/app/_models/ph-calibration';
import { User } from 'src/app/_models/user';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { AuthEventService } from 'src/app/_services/auth-event.service';
import { CanComponentDeactivate } from 'src/app/_guards/can-deactivate.guard';
import {
  EDIT_ACCESS_ROLE_LIST,
  LAB_ADMIN_ROLE_LIST,
  Role,
} from 'src/app/_models/role';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import {
  EMPTY,
  Observable,
  filter,
  interval,
  map,
  merge,
  race,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { PhCalibrationGraphComponent } from '../../_components/graph/ph-calibration-graph/ph-calibration-graph.component';
import { OxygenCalibrationGraphComponent } from '../../_components/graph/oxygen-calibration-graph/oxygen-calibration-graph.component';
import { SpargingCalibrationGraphComponent } from 'src/app/_components/graph/sparging-calibration-graph/sparging-calibration-graph.component';
import { LoadingSpinnerComponent } from '../../_components/loading-spinner/loading-spinner.component';
import { CalibrationGraphComponent } from '../../_components/graph/calibration-graph/calibration-graph.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { StatusIndicatorComponent } from '../../_components/chips/status-indicator/status-indicator.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { DatePipe } from '@angular/common';
import { ReducedSmallDataCardComponent } from '../../_components/cards/reduced-small-data-card/reduced-small-data-card.component';
import { Laboratory } from 'src/app/_models/laboratory';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { SpargingCalibration } from 'src/app/_models/sparging-calibration';
import { DropdownFormFieldComponent } from 'src/app/_components/forms/dropdown-form-field/dropdown-form-field.component';
import { CalibrationChangelogEntry } from 'src/app/_models/changelog';
import { CalibrationChangelogResponse } from 'src/app/_models/api-responses';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { SlideToggleComponent } from 'src/app/_components/selection-controls/slide-toggle/slide-toggle.component';
import { FlaskCardComponent } from 'src/app/_components/cards/flask-card/flask-card.component';
import { ChipRadioButtonComponent } from '../../_components/selection-controls/chip-radio-button/chip-radio-button.component';
import {
  TableInputComponent,
  OdCellNavigateEvent,
} from 'src/app/_components/forms/table-input/table-input.component';

const OD_FLASK_COLORS = ['#2f7fc1', '#d1a238', '#4fae94', '#8177c9'] as const;

@Component({
  selector: 'app-calibration-data',
  templateUrl: './calibration-data.component.html',
  styleUrls: ['./calibration-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconButtonComponent,
    StatusIndicatorComponent,
    ButtonComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    CalibrationGraphComponent,
    LoadingSpinnerComponent,
    OxygenCalibrationGraphComponent,
    SpargingCalibrationGraphComponent,
    PhCalibrationGraphComponent,
    ValidationModalComponent,
    ModalComponent,
    DatePipe,
    RoleCheckPipe,
    ReducedSmallDataCardComponent,
    ErrorPageComponent,
    DropdownFormFieldComponent,
    MatIcon,
    MatTableModule,
    SlideToggleComponent,
    MatIconButtonComponent,
    FlaskCardComponent,
    ChipRadioButtonComponent,
    TableInputComponent,
  ],
})
export class CalibrationDataComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private readonly route = inject(ActivatedRoute);
  private readonly calibrationService = inject(CalibrationService);
  private readonly dataService = inject(DataService);
  private readonly modalService = inject(ModalService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthenticationService);
  private readonly authEventService = inject(AuthEventService);

  readonly changelogDisplayedColumns = ['date', 'note', 'change', 'user'];
  readonly odDisplayedColumns = [
    'reactor',
    'col-1',
    'col-2',
    'col-3',
    'col-4',
    'col-5',
    'col-6',
  ] as const;

  // Control vars
  readonly fetchCalibrationError = signal<ErrorObject | null>(null);
  readonly dataError = signal<ErrorObject | null>(null);
  readonly pollingError = signal<ErrorObject | null>(null);
  readonly discardError = signal<ErrorObject | null>(null);
  readonly voltageError = signal<ErrorObject | null>(null);
  readonly changelogError = signal<ErrorObject | null>(null);
  readonly notificationError = signal<ErrorObject | null>(null);
  readonly loading = signal(false);
  private dataFirstLoad = true;
  readonly dataIsProcessing = signal(false);
  readonly discardIsProcessing = signal(false);
  readonly notificationsIsProcessing = signal(false);
  readonly currentUser = signal<User | null>(null);
  readonly laboratory = signal<Laboratory | null>(null);
  // undefined = not yet known (calibration hasn't loaded), distinct from a
  // confirmed false, so a not-yet-loaded page can't be mistaken for "not finalised".
  readonly finalisedMessageSentToDevice = signal<boolean | undefined>(
    undefined,
  );
  private readonly unsubscribe$ = new Subject<void>();
  private readonly subscriptionsReset$ = new Subject<void>();
  private readonly allowNavigation = new Subject<boolean>();
  private pendingNavigationPromise: Promise<boolean> | null = null;

  // Table vars
  readonly changelogList = signal<CalibrationChangelogEntry[]>([]);
  readonly currentSort = signal({
    sortBy: 'CreatedAt',
    sortDirection: 'asc',
  });

  // Pagination
  private pageRequested = 1;
  readonly pageSize = signal(5);
  readonly totalPages = signal(0);
  readonly currentPage = signal(1);
  readonly hasNext = signal(false);
  readonly hasPrevious = signal(false);

  protected readonly CalibrationTypeEnum = CalibrationTypeEnum;

  // Discard/delete calibration modals
  readonly validationTitleText = signal('Delete calibration?');

  // Make role enum available to template
  protected readonly Role = Role;
  readonly editAccessRoles = EDIT_ACCESS_ROLE_LIST;

  /** Whether the current user may edit OD reading cells right now. */
  readonly canEditOdReadings = computed(() => {
    const role = this.currentUser()?.role;
    return (
      !!role &&
      this.editAccessRoles.includes(role) &&
      !this.finalisedMessageSentToDevice()
    );
  });

  readonly calibrationType = signal('');
  readonly calibrationTypeId = signal<CalibrationTypeEnum | null>(null);
  readonly calibrationId = signal('');

  readonly odCalibration = signal<CalibrationResults | null>(null);
  readonly voltageReadings = signal<VoltageReadings | null>(null);
  private voltagePollingSubscription: Subscription | null = null;
  private calibrationPollingSubscription: Subscription | null = null;
  private oxygenPollingSubscription: Subscription | null = null;
  private phPollingSubscription: Subscription | null = null;
  private spargingPollingSubscription: Subscription | null = null;
  readonly isVoltagePolling = signal(false);
  readonly isCalibrationPolling = signal(false);
  readonly isOxygenPolling = signal(false);
  readonly isPhPolling = signal(false);
  readonly isSpargingPolling = signal(false);
  readonly voltageLabel = signal('Poll for voltage');
  readonly calibrationLabel = signal('Poll for calibration data');

  minODValue = 0;
  maxODValue = 10;
  odStep = 0.01;
  readonly finalisationIsProcessing = signal(false);
  readonly finalisationError = signal<ErrorObject | null>(null);
  readonly saveReadingsIsProcessing = signal(false);
  readonly unsavedOdCachedData = signal<FinaliseCalibrationInputSet | null>(
    null,
  );

  readonly oxygenCalibration = signal<OxygenCalibration | null>(null);
  readonly phCalibration = signal<PhCalibration | null>(null);
  readonly spargingCalibration = signal<SpargingCalibration | null>(null);
  readonly calibrationLoadingText = 'Fetching calibration data...';

  // Oxygen vars
  readonly oxygenSaturation = signal(1);
  readonly oxygenDataReloading = signal(false);
  readonly notificationModalTitle = signal('');
  readonly notificationModalText = signal('');

  readonly calibrationData = signal<ICalibrationDevice>({
    deviceId: 0,
    flasks: [],
  });
  readonly oxygenCalibrationData = signal<IOxygenCalibrationDevice>({
    deviceId: 0,
    flasks: [],
  });
  readonly phCalibrationData = signal<IPhCalibrationDevice>({
    deviceId: 0,
    flasks: [],
  });
  readonly spargingCalibrationData = signal<ISpargingCalibrationDevice>({
    deviceId: 0,
    flasks: [],
  });
  readonly renderCalibrationGraph = signal(false);
  readonly timeOfLastUpdate = signal<Date | null>(null);

  // Pagination form controls
  changeLogsPerPageField = new FormBase({
    key: 'changeLogsPerPage',
    label: 'Page size',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });
  paginationControls = this.formBuilder.group({
    [this.changeLogsPerPageField.key]: [this.pageSize().toString()],
  });

  /**
   * The OD form's control keys, the FinaliseCalibrationInputSet reading keys, and
   * the VoltageReadings voltage keys all address the same (flask, data-point) grid
   * using three different naming conventions. Centralising the conversions here
   * means a future rename only needs to change one place, instead of one of the
   * scattered ad hoc string replacements silently no-op'ing.
   */
  private odFormKey(flask: string, n: number): string {
    return `flask${flask}Value${n}`;
  }

  /** The OD reading control for a given form key, typed for app-table-input. */
  odControl(key: string): FormControl {
    return this.odCalibrationForm.get(key) as FormControl;
  }

  private odReadingKey(
    flask: string,
    n: number,
  ): keyof FinaliseCalibrationInputSet {
    return `flask${flask}Reading${n}` as keyof FinaliseCalibrationInputSet;
  }

  private parseOdVoltageKey(key: string): { flask: string; n: number } | null {
    const match = /^flask([A-D])VoltageValue([1-6])$/.exec(key);
    return match ? { flask: match[1], n: Number(match[2]) } : null;
  }

  private parseOdReadingKey(key: string): { flask: string; n: number } | null {
    const match = /^flask([A-D])Reading([1-6])$/.exec(key);
    return match ? { flask: match[1], n: Number(match[2]) } : null;
  }

  readonly odFlaskFields = ['A', 'B', 'C', 'D'].map((flask, i) => ({
    label: `Reactor ${flask} Input Values`,
    flaskId: flask,
    color: OD_FLASK_COLORS[i],
    voltagePrefix: `flask${flask}VoltageValue`,
    fields: [1, 2, 3, 4, 5, 6].map(
      (n) =>
        new FormBase<number>({
          key: this.odFormKey(flask, n),
          label: `Reactor ${flask} OD Value ${n}`,
          type: 'number',
          placeholder: '',
          disabled: true,
          required: false,
          value: 0,
        }),
    ),
  }));

  odCalibrationForm = this.formBuilder.group(
    Object.fromEntries(
      this.odFlaskFields
        .flatMap((flask) => flask.fields)
        .map((field) => [
          field.key,
          [
            { value: null, disabled: true },
            [
              Validators.required,
              Validators.min(this.minODValue),
              Validators.max(this.maxODValue),
              stepValidator(this.odStep),
            ],
          ],
        ]),
    ),
  );

  // Radio group for selecting oxygen saturation percentage (0% or 100%)
  saturationToggle = new FormBase<string>({
    key: 'saturationToggle',
    label: 'Select oxygen saturation percentage',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: true,
    value: '',
    options: [
      { key: 1, value: '100%' },
      { key: 0, value: '0%' },
    ],
  });

  // Reactive form that holds the oxygen saturation toggle selection (0% or 100%); changes handled via handleOxygenSaturationToggle()
  selectSaturationForm = this.formBuilder.group({
    [this.saturationToggle.key]: [1, []],
  });

  toggleOxygenNotifications = new FormBase<boolean>({
    key: 'oxygenNotifications',
    label: 'Receive email notifications',
    type: 'toggle',
    placeholder: '',
    disabled: true,
    required: false,
    value: false,
    options: [],
  });

  oxygenNotificationsForm = this.formBuilder.group({
    [this.toggleOxygenNotifications.key]: [false, []],
  });

  readonly activeCalibration = computed<ActiveCalibrationSummary | null>(() => {
    switch (this.calibrationType()) {
      case 'optical-density':
        return this.odCalibration() ?? null;
      case 'oxygen':
        return this.oxygenCalibration() ?? null;
      case 'ph':
        return this.phCalibration() ?? null;
      case 'sparging':
        return this.spargingCalibration() ?? null;
      default:
        return null;
    }
  });

  readonly activeCalibrationIsFinal = computed(
    () => !!this.activeCalibration()?.isFinal,
  );

  readonly activeCalibrationIsDeleted = computed(
    () => !!this.activeCalibration()?.isDeleted,
  );

  readonly calibrationTitle = computed(() => {
    switch (this.calibrationType()) {
      case 'optical-density':
        return `Optical Density Calibration - ${this.odCalibration()?.deviceName ?? ''}`;
      case 'oxygen':
        return `Oxygen Saturation Calibration - ${this.oxygenCalibration()?.deviceName ?? ''}`;
      case 'ph':
        return `pH Calibration - ${this.phCalibration()?.deviceName ?? ''}`;
      case 'sparging':
        return `Sparging Calibration - ${this.spargingCalibration()?.deviceName ?? ''}`;
      default:
        return '';
    }
  });

  readonly deletePermittedRoles = computed(() =>
    this.calibrationType() === 'optical-density'
      ? LAB_ADMIN_ROLE_LIST
      : EDIT_ACCESS_ROLE_LIST,
  );

  ngOnInit(): void {
    this.currentUser.set(this.authService.currentUserValue);
    this.laboratory.set(this.authService.selectedLaboratory);

    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
      this.calibrationType.set(params.calibrationType as string);
      this.calibrationId.set(params.id as string);
      this.unsavedOdCachedData.set(
        this.calibrationService.retrieveAndClearUnsavedOdFormCache(
          this.calibrationType(),
          this.calibrationId(),
        ),
      );
      this.initialiseSubscriptions();
      this.getCalibrationFromApi(this.calibrationId(), this.calibrationType());
    });
  }

  getCalibrationFromApi(calibrationId: string, calibrationType: string): void {
    this.loading.set(true);
    this.fetchCalibrationError.set(null);
    this.dataFirstLoad = true;
    switch (calibrationType) {
      case 'optical-density':
        this.calibrationService
          .getODCalibrationById(calibrationId)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: CalibrationResults) => {
              this.odCalibration.set(response);
              this.calibrationTypeId.set(CalibrationTypeEnum.BaseModule);
              // If the calibration data has not been loaded yet, load it
              if (this.dataFirstLoad) {
                this.dataFirstLoad = false;
                this.finalisedMessageSentToDevice.set(
                  !!response.finalisedMessageSentToDevice,
                );
                this.fetchVoltageReadingsAndLoad(response.id);
              } else {
                this.loading.set(false);
              }
            },
            error: (error: ErrorObject) => {
              this.recacheUnsavedOdFormDataIfPresent();
              this.fetchCalibrationError.set(error);
              this.loading.set(false);
            },
          });
        break;
      case 'oxygen':
        this.calibrationService
          .getOxygenCalibrationById(calibrationId)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: OxygenCalibration) => {
              this.oxygenCalibration.set(response);
              this.calibrationTypeId.set(CalibrationTypeEnum.Oxygen);
              this.updateOxygenNotificationsToggleState();
              if (this.dataFirstLoad) {
                this.dataFirstLoad = false;
                this.getCalibrationDataFromApi();
              } else {
                this.loading.set(false);
              }
            },
            error: (error: ErrorObject) => {
              this.fetchCalibrationError.set(error);
              this.loading.set(false);
            },
          });
        break;
      case 'ph':
        this.calibrationService
          .getPhCalibrationById(calibrationId)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: PhCalibration) => {
              this.phCalibration.set(response);
              this.calibrationTypeId.set(CalibrationTypeEnum.pH);
              // If the data hasn't been loaded yet, load it
              if (this.dataFirstLoad) {
                this.dataFirstLoad = false;
                this.getCalibrationDataFromApi();
              } else {
                this.loading.set(false);
              }
            },
            error: (error: ErrorObject) => {
              this.fetchCalibrationError.set(error);
              this.loading.set(false);
            },
          });
        break;
      case 'sparging':
        this.calibrationService
          .getSpargingCalibrationById(calibrationId)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: SpargingCalibration) => {
              this.spargingCalibration.set(response);
              this.calibrationTypeId.set(CalibrationTypeEnum.Sparging);
              // If the data hasn't been loaded yet, load it
              if (this.dataFirstLoad) {
                this.dataFirstLoad = false;
                this.getCalibrationDataFromApi();
              } else {
                this.loading.set(false);
              }
            },
            error: (error: ErrorObject) => {
              this.fetchCalibrationError.set(error);
              this.loading.set(false);
            },
          });
        break;
      default:
        this.fetchCalibrationError.set({
          status: 0,
          message: `Unknown calibration type: "${calibrationType}". Please return to the dashboard.`,
          code: null,
        });
        this.loading.set(false);
        break;
    }
  }

  getCalibrationDataFromApi(): void {
    this.dataError.set(null);
    this.dataIsProcessing.set(true);
    this.dataFirstLoad = false;
    switch (this.calibrationType()) {
      case 'optical-density':
        this.dataService
          .getODCalibrationData(parseInt(this.calibrationId()))
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: ICalibrationDevice) => {
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
              this.calibrationData.set(response);
              if (this.calibrationData().flasks?.length > 0) {
                this.renderCalibrationGraph.set(true);
              }
              this.getCalibrationChangelog();
            },
            error: (error: ErrorObject) => {
              this.dataError.set(error);
              this.loading.set(false);
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
            },
          });
        break;
      case 'oxygen':
        this.dataService
          .getOxygenCalibrationData(
            parseInt(this.calibrationId()),
            this.oxygenSaturation(),
          )
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: IOxygenCalibrationDevice) => {
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
              this.oxygenCalibrationData.set(response);

              if (this.oxygenCalibrationData().flasks?.length > 0) {
                this.renderCalibrationGraph.set(true);
              }
              this.getCalibrationChangelog();
            },
            error: (error: ErrorObject) => {
              this.dataError.set(error);
              this.loading.set(false);
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
            },
          });
        break;
      case 'ph':
        this.dataService
          .getPhCalibrationData(parseInt(this.calibrationId()))
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: IPhCalibrationDevice) => {
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
              this.phCalibrationData.set(response);
              if (this.phCalibrationData().flasks?.length > 0) {
                this.renderCalibrationGraph.set(true);
              }
              this.getCalibrationChangelog();
            },
            error: (error: ErrorObject) => {
              this.dataError.set(error);
              this.loading.set(false);
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
            },
          });
        break;
      case 'sparging':
        this.dataService
          .getSpargingCalibrationData(parseInt(this.calibrationId()))
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: ISpargingCalibrationDevice) => {
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
              this.spargingCalibrationData.set(response);
              if (this.spargingCalibrationData().flasks?.length > 0) {
                this.renderCalibrationGraph.set(true);
              }
              this.getCalibrationChangelog();
            },
            error: (error: ErrorObject) => {
              this.dataError.set(error);
              this.loading.set(false);
              this.dataIsProcessing.set(false);
              this.timeOfLastUpdate.set(new Date());
            },
          });
        break;
      default:
        this.dataError.set({
          status: 0,
          message: `Cannot load data for unknown type: "${this.calibrationType()}".`,
          code: null,
        });
        this.loading.set(false);
        this.dataIsProcessing.set(false);
        break;
    }
  }

  // Changelog page size
  initialiseSubscriptions(): void {
    this.subscriptionsReset$.next();

    const untilReset$ = merge(this.unsubscribe$, this.subscriptionsReset$);

    this.paginationControls
      .get(this.changeLogsPerPageField.key)
      .valueChanges.pipe(takeUntil(untilReset$))
      .subscribe((numOfChangelogs) => {
        this.pageSize.set(Number.parseInt(numOfChangelogs as string, 10));
        this.pageRequested = 1;
        this.getCalibrationChangelog();
      });

    this.oxygenNotificationsForm
      .get(this.toggleOxygenNotifications.key)
      .valueChanges.pipe(takeUntil(untilReset$))
      .subscribe((value) => {
        this.setOxygenCalibrationNotifications(value);
      });
  }

  editCalibration(): void {
    void this.router.navigate(
      [`/calibration/${this.calibrationType()}/${this.calibrationId()}/edit`],
      {
        state: {
          data:
            this.odCalibration() ||
            this.oxygenCalibration() ||
            this.phCalibration() ||
            this.spargingCalibration(),
        },
      },
    );
  }

  handleDiscardAndDelete(action: string): void {
    if (action === 'discard') {
      this.validationTitleText.set('Confirm Discard');
    } else if (action === 'delete') {
      this.validationTitleText.set('Delete calibration?');
    }
    this.openModal('delete-calibration');
  }

  discardCalibration(): void {
    if (this.discardIsProcessing()) {
      return;
    }
    this.discardIsProcessing.set(true);
    this.discardError.set(null);
    // Ensure that all polling is stopped
    this.stopVoltagePolling();
    this.stopCalibrationPolling();
    this.stopOxygenPolling();
    this.stopPHPolling();
    this.stopSpargingPolling();

    const config = this.resolveDeleteConfig();
    if (!config) {
      this.discardError.set({
        status: 0,
        message: 'Unable to delete calibration: unknown calibration type.',
        code: null,
      });
      this.discardIsProcessing.set(false);
      this.closeModal('delete-calibration');
      this.openModal('discard-error-response');
      return;
    }

    config.delete$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (success: boolean) => {
        this.discardIsProcessing.set(false);
        this.closeModal('delete-calibration');
        if (success) {
          config.onSuccess();
          this.loading.set(false);
          this.openModal('discard-success-response');
        } else {
          this.discardError.set({
            status: 0,
            message: 'Unable to delete calibration.',
            code: null,
          });
          this.openModal('discard-error-response');
        }
      },
      error: (error: ErrorObject) => {
        this.discardError.set(error);
        this.discardIsProcessing.set(false);
        this.closeModal('delete-calibration');
        this.openModal('discard-error-response');
      },
    });
  }

  private resolveDeleteConfig(): {
    delete$: Observable<boolean>;
    onSuccess: () => void;
  } | null {
    switch (this.calibrationType()) {
      case 'optical-density': {
        const cal = this.odCalibration();
        if (!cal) return null;
        return {
          delete$: this.calibrationService.deleteODCalibrationById(
            cal.id.toString(),
          ),
          onSuccess: () => this.odCalibration.set(null),
        };
      }
      case 'oxygen': {
        const cal = this.oxygenCalibration();
        if (!cal) return null;
        return {
          delete$: this.calibrationService.deleteOxygenCalibrationById(
            cal.id.toString(),
          ),
          onSuccess: () => this.oxygenCalibration.set(null),
        };
      }
      case 'ph': {
        const cal = this.phCalibration();
        if (!cal) return null;
        return {
          delete$: this.calibrationService.deletePhCalibrationById(
            cal.id.toString(),
          ),
          onSuccess: () => this.phCalibration.set(null),
        };
      }
      case 'sparging': {
        const cal = this.spargingCalibration();
        if (!cal) return null;
        return {
          delete$: this.calibrationService.deleteSpargingCalibrationById(
            cal.id.toString(),
          ),
          onSuccess: () => this.spargingCalibration.set(null),
        };
      }
      default:
        return null;
    }
  }

  getVoltageValues(): void {
    // Only implemented for OD calibration as other calibration types don't have voltage readings
    // Added additional calibrationType check for additional security
    if (this.calibrationType() === 'optical-density') {
      this.voltageError.set(null);
      this.isVoltagePolling.set(true);
      this.voltageLabel.set('Polling');

      this.voltagePollingSubscription = interval(30000)
        .pipe(
          startWith(0), // Emit immediately to trigger the first call
          switchMap(() => {
            const cal = this.odCalibration();
            if (!cal) return EMPTY;
            return this.calibrationService.getVoltageReadings(cal.id);
          }),
          tap((response) => {
            this.voltageReadings.set(response);
            if (this.voltageReadings()) {
              this.mapVoltageReadingsToFormControls(this.voltageReadings());
            }
          }),
          switchMap((voltageResponse) => {
            const cal = this.odCalibration();
            if (!cal) return EMPTY;
            return this.calibrationService
              .getODCalibrationById(cal.id.toString())
              .pipe(
                tap((calResponse: CalibrationResults) => {
                  this.odCalibration.set(calResponse);
                  this.finalisedMessageSentToDevice.set(
                    !!calResponse.finalisedMessageSentToDevice,
                  );
                }),
                map(() => voltageResponse),
              );
          }),
          takeWhile((response) => this.shouldContinueVoltagePolling(response)),
          takeUntil(this.unsubscribe$),
        )
        .subscribe({
          complete: () => {
            this.voltageLabel.set('Polling Complete');
          },
          error: (error: ErrorObject) => {
            this.voltageError.set(error);
            this.stopVoltagePolling();
            this.openModal('voltage-error');
          },
        });
    }
  }

  mapVoltageReadingsToFormControls(voltageReadings: VoltageReadings): void {
    Object.keys(voltageReadings).forEach((key) => {
      const coordinate = this.parseOdVoltageKey(key);
      if (!coordinate) return;
      const formControl = this.odCalibrationForm.get(
        this.odFormKey(coordinate.flask, coordinate.n),
      );

      if (formControl) {
        if (voltageReadings[key] == null) {
          formControl.disable(); // Disable if the voltage reading is null
        } else {
          formControl.enable(); // Enable if the voltage reading is not null
        }
      }
    });
    // Enabling/disabling a control changes its required-validity without any
    // event firing inside the cell's own view, so force OnPush to re-check it.
    this.tableInputRefs().forEach((ref) => ref.markEdited());
  }

  finaliseCalibration(): void {
    this.finalisationError.set(null);
    // Only implemented for OD calibration but likely to be implemented for other calibration types
    switch (this.calibrationType()) {
      case 'optical-density': {
        const cal = this.odCalibration();
        if (!cal) {
          this.finalisationError.set({
            status: 0,
            message: 'Unable to finalise: calibration data is not loaded.',
            code: null,
          });
          this.openModal('finalisation-error');
          return;
        }
        this.finalisationIsProcessing.set(true);
        this.calibrationService
          .finaliseODCalibrationById(
            cal.id.toString(),
            Object.fromEntries(
              this.odFlaskFields.flatMap((flask) =>
                flask.fields.map((field, i) => [
                  this.odReadingKey(flask.flaskId, i + 1),
                  this.odCalibrationForm.get(field.key).value as number,
                ]),
              ),
            ) as FinaliseCalibrationInputSet,
          )
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: () => {
              this.finalisationIsProcessing.set(false);
              this.finalisedMessageSentToDevice.set(true);
              this.clearODFormUnsavedHighlight();
              this.startCalibrationPolling();
            },
            error: (error: ErrorObject) => {
              this.finalisationError.set(error);
              this.finalisationIsProcessing.set(false);
              this.openModal('finalisation-error');
            },
          });
        break;
      }
      case 'oxygen':
        break;
      case 'ph':
        break;
      default:
        break;
    }
  }

  fetchVoltageReadingsAndLoad(calibrationId: number): void {
    this.calibrationService
      .getVoltageReadings(calibrationId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (voltageResponse: VoltageReadings) => {
          this.voltageReadings.set(voltageResponse);
          this.mapVoltageReadingsToFormControls(voltageResponse);
          if (this.unsavedOdCachedData()) {
            this.restoreOdFormFromCache();
            this.getCalibrationDataFromApi();
          } else if (Object.values(voltageResponse).some((v) => v != null)) {
            this.getODReadings();
          } else {
            this.getCalibrationDataFromApi();
          }
        },
        error: (error: ErrorObject) => {
          this.voltageError.set(error);
          this.openModal('voltage-error');
          if (this.unsavedOdCachedData()) {
            this.restoreOdFormFromCache();
          }
          this.getCalibrationDataFromApi();
        },
      });
  }

  /**
   * Restores the OD input values that were cached before an unauthorised
   * navigation (see ngOnDestroy). Unlike populateODReadingsForm the form is
   * left dirty so the canDeactivate guard fires correctly if the user tries to
   * navigate away again without saving.
   */
  private restoreOdFormFromCache(): void {
    const readings = this.unsavedOdCachedData();
    if (!readings) {
      return;
    }
    Object.entries(readings).forEach(([key, value]) => {
      const coordinate = this.parseOdReadingKey(key);
      if (!coordinate) {
        return;
      }
      const control = this.odCalibrationForm.get(
        this.odFormKey(coordinate.flask, coordinate.n),
      );
      if (control) {
        control.setValue(value as number);
        control.markAsDirty();
      }
    });
    this.tableInputRefs().forEach((ref) => ref.markEdited());
    this.unsavedOdCachedData.set(null);
  }

  // Puts cached OD readings back into the service when the calibration they
  // belong to fails to load, so a retry on the same page (e.g. a reload) can
  // still recover them instead of losing them along with the failed request.
  private recacheUnsavedOdFormDataIfPresent(): void {
    const cached = this.unsavedOdCachedData();
    if (!cached) {
      return;
    }
    this.calibrationService.cacheUnsavedOdFormData(
      this.calibrationType(),
      this.calibrationId(),
      cached,
    );
    this.unsavedOdCachedData.set(null);
  }

  populateODReadingsForm(readings: FinaliseCalibrationInputSet | null): void {
    if (!readings) {
      return;
    }
    Object.entries(readings).forEach(([key, value]) => {
      const coordinate = this.parseOdReadingKey(key);
      if (!coordinate) return;
      const control = this.odCalibrationForm.get(
        this.odFormKey(coordinate.flask, coordinate.n),
      );
      if (control) {
        control.setValue(value as number);
      }
    });
    this.clearODFormUnsavedHighlight();
  }

  // Values currently in the form have just been persisted (via a save or a
  // finalise), so clear the "unsaved edit" highlight rather than leaving it
  // dirty forever.
  private clearODFormUnsavedHighlight(): void {
    this.odCalibrationForm.markAsPristine();
    this.tableInputRefs().forEach((ref) => ref.markEdited());
  }

  getODReadings(): void {
    this.calibrationService
      .getODCalibrationReadings(this.calibrationId())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: FinaliseCalibrationInputSet | null) => {
          this.populateODReadingsForm(response);
          this.getCalibrationDataFromApi();
        },
        error: (error: ErrorObject) => {
          this.finalisationError.set(error);
          this.openModal('finalisation-error');
          this.getCalibrationDataFromApi();
        },
      });
  }

  saveReadings(): void {
    this.finalisationError.set(null);
    const cal = this.odCalibration();
    if (!cal) {
      this.finalisationError.set({
        status: 0,
        message: 'Unable to save readings: calibration data is not loaded.',
        code: null,
      });
      this.openModal('finalisation-error');
      return;
    }
    this.saveReadingsIsProcessing.set(true);
    this.calibrationService
      .saveODCalibrationReadings(
        cal.id.toString(),
        Object.fromEntries(
          this.odFlaskFields.flatMap((flask) =>
            flask.fields.map((field, i) => [
              this.odReadingKey(flask.flaskId, i + 1),
              this.odCalibrationForm.get(field.key).value as number,
            ]),
          ),
        ) as FinaliseCalibrationInputSet,
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: FinaliseCalibrationInputSet) => {
          this.populateODReadingsForm(response);
          this.saveReadingsIsProcessing.set(false);
        },
        error: (error: ErrorObject) => {
          this.finalisationError.set(error);
          this.saveReadingsIsProcessing.set(false);
          this.openModal('finalisation-error');
        },
      });
  }

  startCalibrationPolling(): void {
    this.stopVoltagePolling();
    this.calibrationPollingSubscription = this.buildPollingSubscription(
      this.isCalibrationPolling,
      () => {
        const cal = this.odCalibration();
        if (!cal) return EMPTY;
        return this.dataService.getODCalibrationData(cal.id);
      },
      this.calibrationData,
      () => {
        const cal = this.odCalibration();
        if (!cal) return EMPTY;
        return this.calibrationService.getODCalibrationById(cal.id.toString());
      },
      this.odCalibration,
    );
  }

  startOxygenPolling(): void {
    this.oxygenPollingSubscription = this.buildPollingSubscription(
      this.isOxygenPolling,
      () =>
        this.dataService.getOxygenCalibrationData(
          parseInt(this.calibrationId()),
          this.oxygenSaturation(),
        ),
      this.oxygenCalibrationData,
      () => {
        const cal = this.oxygenCalibration();
        if (!cal) return EMPTY;
        return this.calibrationService.getOxygenCalibrationById(
          cal.id.toString(),
        );
      },
      this.oxygenCalibration,
      {
        afterSetCalibration: () => {
          this.oxygenDataReloading.set(false);
          this.updateOxygenNotificationsToggleState();
        },
        clearGraphOnNoFlasks: true,
        onError: () => this.oxygenDataReloading.set(false),
      },
    );
  }

  startPHPolling(): void {
    this.phPollingSubscription = this.buildPollingSubscription(
      this.isPhPolling,
      () =>
        this.dataService.getPhCalibrationData(parseInt(this.calibrationId())),
      this.phCalibrationData,
      () => {
        const cal = this.phCalibration();
        if (!cal) return EMPTY;
        return this.calibrationService.getPhCalibrationById(cal.id.toString());
      },
      this.phCalibration,
    );
  }

  startSpargingPolling(): void {
    this.spargingPollingSubscription = this.buildPollingSubscription(
      this.isSpargingPolling,
      () =>
        this.dataService.getSpargingCalibrationData(
          parseInt(this.calibrationId()),
        ),
      this.spargingCalibrationData,
      () => {
        const cal = this.spargingCalibration();
        if (!cal) return EMPTY;
        return this.calibrationService.getSpargingCalibrationById(
          cal.id.toString(),
        );
      },
      this.spargingCalibration,
    );
  }

  private buildPollingSubscription<
    TData extends { flasks?: unknown[] },
    TCal extends { isFinal?: boolean },
  >(
    isPollingSignal: WritableSignal<boolean>,
    getDataFn: () => Observable<TData>,
    dataSignal: WritableSignal<TData>,
    getCalFn: () => Observable<TCal>,
    calSignal: WritableSignal<TCal>,
    options: {
      afterSetCalibration?: () => void;
      clearGraphOnNoFlasks?: boolean;
      onError?: () => void;
    } = {},
  ): Subscription {
    const {
      afterSetCalibration,
      clearGraphOnNoFlasks = false,
      onError,
    } = options;
    isPollingSignal.set(true);
    this.calibrationLabel.set('Polling for calibration data');
    return interval(30000)
      .pipe(
        startWith(0),
        switchMap(() => getDataFn()),
        tap((response: TData) => {
          this.timeOfLastUpdate.set(new Date());
          dataSignal.set(response);
          if (response.flasks?.length > 0) {
            this.renderCalibrationGraph.set(true);
          } else if (clearGraphOnNoFlasks) {
            this.renderCalibrationGraph.set(false);
          }
        }),
        switchMap(() => getCalFn()),
        tap((response: TCal) => {
          calSignal.set(response);
          afterSetCalibration?.();
        }),
        switchMap(() =>
          this.calibrationService.getCalibrationChangelog(
            this.calibrationTypeId(),
            this.calibrationId(),
            this.currentSort().sortBy,
            this.currentSort().sortDirection,
            this.pageSize(),
            this.pageRequested,
          ),
        ),
        tap((response) => {
          this.changelogError.set(null);
          this.changelogList.set(response.changelogList);
          // Set pagination vars from response headers
          this.currentPage.set(response.paginationData.CurrentPage);
          this.pageSize.set(response.paginationData.PageSize);
          this.totalPages.set(response.paginationData.TotalPages);
          this.hasNext.set(response.paginationData.HasNext);
          this.hasPrevious.set(response.paginationData.HasPrevious);
          this.loading.set(false);
        }),
        // takeWhile must be placed after calSignal.set() so it evaluates the updated isFinal value
        takeWhile(() => !calSignal()?.isFinal),
      )
      .subscribe({
        complete: () => {
          this.calibrationLabel.set('Polling Complete');
          isPollingSignal.set(false);
        },
        error: (error: ErrorObject) => {
          this.pollingError.set(error);
          this.loading.set(false);
          this.dataIsProcessing.set(false);
          this.timeOfLastUpdate.set(new Date());
          isPollingSignal.set(false);
          this.calibrationLabel.set('Poll for calibration data');
          onError?.();
          this.openModal('calibration-polling-error');
        },
      });
  }

  handleOxygenSaturationToggle(oxygenSaturationValue: number): void {
    this.renderCalibrationGraph.set(false);
    this.oxygenDataReloading.set(true);
    this.oxygenSaturation.set(oxygenSaturationValue);
    this.stopOxygenPolling();
    this.startOxygenPolling();
  }

  stopCalibrationPolling(): void {
    this.calibrationPollingSubscription = this.stopPollingSubscription(
      this.calibrationPollingSubscription,
      this.isCalibrationPolling,
    );
  }

  stopOxygenPolling(): void {
    this.oxygenPollingSubscription = this.stopPollingSubscription(
      this.oxygenPollingSubscription,
      this.isOxygenPolling,
    );
  }

  stopPHPolling(): void {
    this.phPollingSubscription = this.stopPollingSubscription(
      this.phPollingSubscription,
      this.isPhPolling,
    );
  }

  stopSpargingPolling(): void {
    this.spargingPollingSubscription = this.stopPollingSubscription(
      this.spargingPollingSubscription,
      this.isSpargingPolling,
    );
  }

  private stopPollingSubscription(
    subscription: Subscription | null,
    isPollingSignal: WritableSignal<boolean>,
  ): null {
    if (subscription) {
      subscription.unsubscribe();
      isPollingSignal.set(false);
      this.calibrationLabel.set('Poll for calibration data');
    }
    return null;
  }

  stopVoltagePolling() {
    if (this.voltagePollingSubscription) {
      this.voltagePollingSubscription.unsubscribe();
      this.voltagePollingSubscription = null;
      this.isVoltagePolling.set(false);
      if (
        !this.voltageReadings() ||
        Object.keys(this.voltageReadings()).length === 0
      ) {
        this.voltageLabel.set('Poll for voltage');
      }
    }
  }

  private updateOxygenNotificationsToggleState(): void {
    const control = this.oxygenNotificationsForm.get(
      this.toggleOxygenNotifications.key,
    );
    if (!control || !this.oxygenCalibration()) return;
    const isCurrentUserSubscribed =
      this.currentUser()?.email?.toLowerCase() ===
      this.oxygenCalibration()?.notificationEmail?.toLowerCase();
    if (
      !this.oxygenCalibration()?.isFinal &&
      (!this.oxygenCalibration()?.notificationEmail || isCurrentUserSubscribed)
    ) {
      control.enable({ emitEvent: false });
      control.setValue(isCurrentUserSubscribed, { emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  getVoltageValue(voltagePrefix: string, n: number): number | undefined {
    return this.voltageReadings()?.[
      `${voltagePrefix}${n}` as keyof VoltageReadings
    ];
  }

  /** True once at least one flask/data-point voltage has actually been received. */
  readonly hasVoltageReadings = computed(() =>
    Object.values(this.voltageReadings() ?? {}).some((v) => v != null),
  );

  // OD table keyboard navigation
  private readonly tableInputRefs = viewChildren(TableInputComponent);

  navigateOdCell(event: OdCellNavigateEvent): void {
    const { direction, row, col } = event;
    // viewChildren returns inputs in DOM order (matching @for order): row-major flat index
    const colCount = this.odFlaskFields[0].fields.length;
    const rowCount = this.odFlaskFields.length;
    let targetRow = row;
    let targetCol = col;

    switch (direction) {
      case 'up':
        targetRow = Math.max(0, row - 1);
        break;
      case 'down':
        targetRow = Math.min(rowCount - 1, row + 1);
        break;
      case 'left':
        targetCol = Math.max(0, col - 1);
        break;
      case 'right':
        targetCol = Math.min(colCount - 1, col + 1);
        break;
    }

    const index = targetRow * colCount + targetCol;
    this.tableInputRefs()[index]?.focus();
  }

  /**
   * Copies Flask A's OD input value for the given column into Flask B, C, and D
   * for the same column. Only copies into enabled controls (i.e. where a voltage
   * reading has already been received). Marks each updated control as dirty so
   * the edited state is reflected in the table. Also calls markEdited() on each
   * affected TableInputComponent so OnPush re-evaluates [class.is-edited].
   */
  copyColumnVoltages(colIndex: number): void {
    const sourceValue = this.odCalibrationForm.get(
      this.odFlaskFields[0].fields[colIndex].key,
    )?.value as number | null;

    if (sourceValue == null) return;

    const colCount = this.odFlaskFields[0].fields.length;
    this.odFlaskFields.slice(1).forEach((flask, sliceIndex) => {
      const control = this.odCalibrationForm.get(flask.fields[colIndex].key);
      if (control && !control.disabled) {
        control.setValue(sourceValue);
        control.markAsDirty();
        // sliceIndex is 0-based into slice(1), so actual row = sliceIndex + 1
        this.tableInputRefs()[
          (sliceIndex + 1) * colCount + colIndex
        ]?.markEdited();
      }
    });
  }

  private shouldContinueVoltagePolling(response: VoltageReadings): boolean {
    if (response === null) {
      return true;
    }
    return !this.finalisedMessageSentToDevice();
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  private isNavigationAllowed(): Promise<boolean> {
    if (
      this.odCalibrationForm.pristine ||
      !this.currentUser() ||
      !this.activeCalibration() ||
      this.authEventService.isUnauthorizedNavigationPending
    ) {
      return Promise.resolve(true);
    }

    if (this.pendingNavigationPromise) {
      return this.pendingNavigationPromise;
    }

    this.pendingNavigationPromise = new Promise<boolean>((resolve) => {
      this.openModal('attention');

      // A forced logout (an unrelated 401 elsewhere) must win over this
      // prompt: without this, the pending promise stays unresolved until the
      // user answers, which can veto or stall the forced navigation to
      // /login triggered while this modal is still open.
      race(
        this.allowNavigation.pipe(
          map((isConfirmed) => ({ isConfirmed, isForcedLogout: false })),
        ),
        this.authEventService.isUnauthorizedNavigationPending$.pipe(
          filter((isPending) => isPending),
          map(() => ({ isConfirmed: true, isForcedLogout: true })),
        ),
      )
        .pipe(take(1), takeUntil(this.unsubscribe$))
        .subscribe(({ isConfirmed, isForcedLogout }) => {
          this.pendingNavigationPromise = null;
          if (isForcedLogout) {
            this.closeModal('attention');
          }
          resolve(isConfirmed);
        });
    });

    return this.pendingNavigationPromise;
  }

  ngOnDestroy(): void {
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.odCalibrationForm.pristine
    ) {
      const formData = Object.fromEntries(
        this.odFlaskFields.flatMap((flask) =>
          flask.fields.map((field, i) => [
            this.odReadingKey(flask.flaskId, i + 1),
            this.odCalibrationForm.get(field.key).value as number,
          ]),
        ),
      ) as FinaliseCalibrationInputSet;
      this.calibrationService.cacheUnsavedOdFormData(
        this.calibrationType(),
        this.calibrationId(),
        formData,
      );
    }

    // Settle any pending navigation guard Promise as "cancel" so the router
    // is not left waiting if component destruction races with guard evaluation.
    this.allowNavigation.next(false);
    this.allowNavigation.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.subscriptionsReset$.next();
    this.subscriptionsReset$.complete();
    this.stopVoltagePolling();
    this.stopCalibrationPolling();
    this.stopOxygenPolling();
    this.stopPHPolling();
    this.stopSpargingPolling();
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    switch (modalId) {
      case 'discard-error-response':
        this.discardError.set(null);
        break;
      case 'notification-error':
        this.notificationError.set(null);
        break;
      case 'voltage-error':
        this.voltageError.set(null);
        break;
      case 'finalisation-error':
        this.finalisationError.set(null);
        break;
      case 'calibration-polling-error':
        this.pollingError.set(null);
        break;
      default:
        break;
    }
    this.modalService.close(modalId);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'cancel-navigation':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
      case 'success-primary-button':
      case 'error-primary-button':
        this.discardError.set(null);
        void this.router.navigate(['/dashboard']);
        break;
      case 'success-secondary-button':
        void this.router.navigate(['calibration/create-calibration']);
        break;
      case 'error-secondary-button':
        this.discardError.set(null);
        this.closeModal('discard-error-response');
        break;
      case 'notifications-close-button':
        this.closeModal('notification-success');
        break;
      case 'notifications-error-button':
        this.closeModal('notification-error');
        break;
      case 'voltage-error-button':
        this.closeModal('voltage-error');
        break;
      case 'finalisation-error-button':
        this.closeModal('finalisation-error');
        break;
      case 'calibration-polling-error':
        this.closeModal('calibration-polling-error');
        break;
      default:
        break;
    }
  }

  onSort(sortTerm: string): void {
    if (this.currentSort().sortBy === sortTerm) {
      this.currentSort.update((s) => ({
        ...s,
        sortDirection: s.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      this.currentSort.set({ sortBy: sortTerm, sortDirection: 'asc' });
    }
    this.getCalibrationChangelog();
  }

  getPrevious(): void {
    this.pageRequested = this.currentPage() - 1;
    this.getCalibrationChangelog();
  }

  getNext(): void {
    this.pageRequested = this.currentPage() + 1;
    this.getCalibrationChangelog();
  }

  getCalibrationChangelog(): void {
    this.changelogError.set(null);
    this.loading.set(true);

    this.calibrationService
      .getCalibrationChangelog(
        this.calibrationTypeId(),
        this.calibrationId(),
        this.currentSort().sortBy,
        this.currentSort().sortDirection,
        this.pageSize(),
        this.pageRequested,
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: CalibrationChangelogResponse) => {
          this.changelogError.set(null);
          this.changelogList.set(response.changelogList);
          // Set pagination vars from response headers
          this.currentPage.set(response.paginationData.CurrentPage);
          this.pageSize.set(response.paginationData.PageSize);
          this.totalPages.set(response.paginationData.TotalPages);
          this.hasNext.set(response.paginationData.HasNext);
          this.hasPrevious.set(response.paginationData.HasPrevious);
          this.loading.set(false);
        },
        error: (error: ErrorObject) => {
          this.changelogError.set(error);
          this.loading.set(false);
        },
      });
  }

  setOxygenCalibrationNotifications(toggleOn: boolean): void {
    if (this.notificationsIsProcessing()) {
      return;
    }

    const cal = this.oxygenCalibration();
    if (!cal) {
      return;
    }

    this.notificationsIsProcessing.set(true);
    this.notificationError.set(null);
    this.oxygenNotificationsForm
      .get(this.toggleOxygenNotifications.key)
      .disable({ emitEvent: false });

    this.calibrationService
      .setOxygenCalibrationNotifications(cal.id, toggleOn)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: CalibrationNotification) => {
          this.notificationsIsProcessing.set(false);
          this.oxygenCalibration.update((cal) =>
            cal ? { ...cal, notificationEmail: response.email } : cal,
          );
          this.oxygenNotificationsForm
            .get(this.toggleOxygenNotifications.key)
            .enable({ emitEvent: false });
          if (response.notificationOn) {
            this.notificationModalTitle.set('Success');
            this.notificationModalText.set(
              'You will be notified when the oxygen calibration is complete.',
            );
          } else {
            this.notificationModalTitle.set('Notifications turned off');
            this.notificationModalText.set(
              'You will not receive a notification when the oxygen calibration is complete.',
            );
          }
          this.openModal('notification-success');
        },
        error: (error: ErrorObject) => {
          this.notificationError.set(error);
          this.notificationsIsProcessing.set(false);
          // Revert toggle to previous state
          this.oxygenNotificationsForm
            .get(this.toggleOxygenNotifications.key)
            .setValue(!toggleOn, { emitEvent: false });
          this.oxygenNotificationsForm
            .get(this.toggleOxygenNotifications.key)
            .enable({ emitEvent: false });
          this.openModal('notification-error');
        },
      });
  }
}
